import React, { useMemo, useState } from "react";
import Table from "../../../components/Table";
import TitleButtons from "../../../components/TitleButtons";
import { useConsumosSuprimentos } from "./consumosData";
import { generatePdfReport } from "../../../utils/pdf/generatePdfReport";

type Row = (string | number | React.ReactNode)[];

type ItemConsumoModal = {
  originalIndex: number;
  row: Row;
};

type ModalState = {
  open: boolean;
  consumoKey: string;
  tecnico: string;
  tipo: string;
  data: string;
  hora: string;
  itens: ItemConsumoModal[];
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1080,
  background: "#ffffff",
  borderRadius: 0,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  border: "1px solid #0c2b53",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #0c2b53",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#111827",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 12px 0",
  fontSize: 14,
  fontWeight: 600,
  color: "#0c2b53",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 12,
  marginBottom: 20,
};

const fieldBoxStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  background: "#ffffff",
};

const fieldLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#0c2b53",
  marginBottom: 4,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#111827",
  fontWeight: 500,
  wordBreak: "break-word",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #0c2b53",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  background: "#FFFFFF",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#ffffff",
  color: "#111827",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

function parseQuantidade(value: any): number {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function parseBRLValue(value: any): number {
  if (typeof value === "number") return value;

  const txt = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const n = Number(txt);
  return Number.isFinite(n) ? n : 0;
}

function buildConsumoKey(row: Row) {
  const tecnico = String(row[0] ?? "").trim();
  const tipo = String(row[1] ?? "").trim();
  const data = String(row[6] ?? "").trim();
  const hora = String(row[7] ?? "").trim();

  return `${tecnico}__${tipo}__${data}__${hora}`;
}

const SuprimentosConsumos: React.FC = () => {
  const headers = [
    "Técnico",
    "Tipo",
    "Código",
    "Nome do Produto",
    "Quantidade",
    "Custo Unitário",
    "Data",
    "Hora",
  ];

  const { rows, loading } = useConsumosSuprimentos();

  const columnWidths = [
    "150px",
    "80px",
    "70px",
    "300px",
    "60px",
    "90px",
    "90px",
    "60px",
  ];

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const [modal, setModal] = useState<ModalState>({
    open: false,
    consumoKey: "",
    tecnico: "",
    tipo: "",
    data: "",
    hora: "",
    itens: [],
  });

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  const footerLines = useMemo(() => {
    let totalQuantidade = 0;

    rows.forEach((row) => {
      totalQuantidade += parseQuantidade(row[4]);
    });

    return [`Total de Itens Consumidos: ${totalQuantidade}`];
  }, [rows]);

  const infoRows = useMemo(() => rows, [rows]);

  const openModalPorConsumo = (rowBase: Row) => {
    const key = buildConsumoKey(rowBase);

    const tecnico = String(rowBase[0] ?? "-");
    const tipo = String(rowBase[1] ?? "-");
    const data = String(rowBase[6] ?? "-");
    const hora = String(rowBase[7] ?? "-");

    const itensDoConsumo: ItemConsumoModal[] = rows
      .map((row, index) => {
        if (buildConsumoKey(row as Row) !== key) return null;

        return {
          originalIndex: index,
          row: row as Row,
        };
      })
      .filter(Boolean) as ItemConsumoModal[];

    setModal({
      open: true,
      consumoKey: key,
      tecnico,
      tipo,
      data,
      hora,
      itens: itensDoConsumo,
    });
  };

  const closeModal = () => {
    setModal({
      open: false,
      consumoKey: "",
      tecnico: "",
      tipo: "",
      data: "",
      hora: "",
      itens: [],
    });
  };

  const modalTotals = useMemo(() => {
    let totalQuantidade = 0;
    let totalCusto = 0;

    for (const item of modal.itens) {
      const qtd = parseQuantidade(item.row[4]);
      const custo = parseBRLValue(item.row[5]);

      totalQuantidade += qtd;
      totalCusto += qtd * custo;
    }

    return {
      totalItens: modal.itens.length,
      totalQuantidade,
      totalCusto,
    };
  }, [modal.itens]);

  const gerarRelatorioDoModal = () => {
    if (!modal.itens.length) return;

    const modalRows = modal.itens.map((item) => item.row);

    generatePdfReport({
      reportTitle: "Relatório de Consumo",
      fileNamePrefix: `Consumo-${modal.tecnico}-${modal.data}`.replace(
        /[^\w\-]+/g,
        "_"
      ),
      subtitle: `Consumo interno - Técnico: ${modal.tecnico} | Tipo: ${modal.tipo} | Data: ${modal.data} ${modal.hora}`,
      headers,
      rows: modalRows,
      custoColIndex: 5,
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        `Itens no consumo: ${modalTotals.totalItens}`,
        `Quantidade total: ${modalTotals.totalQuantidade}`,
        `Custo total estimado: ${modalTotals.totalCusto.toLocaleString(
          "pt-BR",
          {
            style: "currency",
            currency: "BRL",
          }
        )}`,
      ],
    });
  };

  return (
    <div>
      <TitleButtons
        title="Suprimentos - Consumos"
        buttons={[
          {
            label: "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Consumos",
                fileNamePrefix: "Suprimentos-Consumos",
                subtitle:
                  "Relatório de Saída de Materiais para Consumo/Uso Interno",
                headers,
                rows,
                custoColIndex: 5,
                enabledColumns: enabledColumnIndexes,
                footerLines,
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },
        ]}
      />

      {loading ? (
        <div style={{ padding: 16 }}>Carregando consumos…</div>
      ) : (
        <Table
          headers={headers}
          rows={infoRows}
          columnWidths={columnWidths}
          onDoubleClick={(viewIndex, rowData, originalIndex) => {
            const idx = originalIndex ?? viewIndex;
            const row = (rows[idx] ?? rowData) as Row;
            if (!row) return;
            openModalPorConsumo(row);
          }}
        />
      )}

      {modal.open && (
        <div style={overlayStyle} onClick={closeModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>Detalhes do consumo</h3>
                <div style={{ marginTop: 6, color: "#374151", fontSize: 14 }}>
                  <strong>{modal.tecnico}</strong> — {modal.tipo}
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              <h4 style={sectionTitleStyle}>Resumo do consumo</h4>

              <div style={gridStyle}>
                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Técnico</span>
                  <div style={fieldValueStyle}>{modal.tecnico || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Tipo</span>
                  <div style={fieldValueStyle}>{modal.tipo || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Data / Hora</span>
                  <div style={fieldValueStyle}>
                    {modal.data || "-"} {modal.hora || ""}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Itens</span>
                  <div style={fieldValueStyle}>{modalTotals.totalItens}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Quantidade total</span>
                  <div style={fieldValueStyle}>
                    {modalTotals.totalQuantidade}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Custo total estimado</span>
                  <div style={fieldValueStyle}>
                    {modalTotals.totalCusto.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </div>
                </div>
              </div>

              <h4 style={sectionTitleStyle}>Itens do consumo</h4>

              <div
                style={{
                  border: "1px solid #d1d5db",
                  marginBottom: 20,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 14,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#0c2b53", color: "#ffffff" }}>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          textAlign: "left",
                          width: 90,
                        }}
                      >
                        Código
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          textAlign: "left",
                        }}
                      >
                        Produto
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 100,
                          textAlign: "center",
                        }}
                      >
                        Quantidade
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 140,
                          textAlign: "center",
                        }}
                      >
                        Custo Unitário
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 160,
                          textAlign: "center",
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {modal.itens.map((item, idx) => {
                      const qtd = parseQuantidade(item.row[4]);
                      const custo = parseBRLValue(item.row[5]);
                      const total = qtd * custo;

                      return (
                        <tr
                          key={item.originalIndex}
                          style={{
                            background: idx % 2 === 0 ? "#fbfcfe" : "#f6f8fb",
                          }}
                        >
                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                            }}
                          >
                            {String(item.row[2] ?? "-")}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                            }}
                          >
                            {String(item.row[3] ?? "-")}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {String(item.row[4] ?? "-")}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {String(item.row[5] ?? "-")}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {total.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={modalFooterStyle}>
              <div style={{ fontSize: 13, color: "#374151" }}>
                {modal.itens.length} item(ns) neste consumo
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={buttonSecondaryStyle}
                >
                  Fechar
                </button>

                <button
                  type="button"
                  onClick={gerarRelatorioDoModal}
                  style={buttonSecondaryStyle}
                >
                  Gerar relatório deste consumo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuprimentosConsumos;
